import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Theme } from '../../theme';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(247, 147, 26, 0.4); }
  50% { box-shadow: 0 0 30px rgba(247, 147, 26, 0.7); }
`;

const Container = styled.div<{ $x: number; $y: number; $theme: Theme }>`
  position: absolute;
  left: ${p => p.$x}px;
  top: ${p => p.$y}px;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
`;

const InputCard = styled.div<{ $theme: Theme }>`
  background: ${p => p.$theme.surface};
  border: 2px solid ${p => p.$theme.primary};
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  animation: ${pulse} 2s ease-in-out infinite;
  min-width: 200px;
`;

const Title = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-size: 13px;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 11px;
  opacity: 0.7;
  text-align: center;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PriceWrapper = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  background: ${p => p.$theme.background};
  border: 1px solid ${p => p.$theme.border};
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  
  &:focus-within {
    border-color: ${p => p.$theme.primary};
  }
`;

const Prefix = styled.span<{ $theme: Theme }>`
  padding: 8px 10px;
  color: ${p => p.$theme.text};
  font-weight: 600;
  font-size: 16px;
  background: ${p => p.$theme.surface};
`;

const Input = styled.input<{ $theme: Theme }>`
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.$theme.primary};
  width: 100px;
  outline: none;
  
  &::placeholder {
    color: ${p => p.$theme.text};
    opacity: 0.3;
  }
`;

const SubmitBtn = styled.button<{ $theme: Theme; $disabled?: boolean }>`
  background: ${p => p.$disabled ? p.$theme.border : p.$theme.primary};
  color: ${p => p.$disabled ? p.$theme.text : '#000'};
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
`;

const UserBadge = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${p => p.$theme.primary};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  background: rgba(247, 147, 26, 0.15);
  border-radius: 10px;
`;

const SuccessCard = styled.div<{ $theme: Theme }>`
  background: ${p => p.$theme.surface};
  border: 2px solid #4caf50;
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
  min-width: 180px;
`;

const SuccessIcon = styled.div`
  font-size: 28px;
  margin-bottom: 6px;
`;

const SuccessPrice = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-size: 22px;
  font-weight: 700;
`;

const SuccessText = styled.div<{ $theme: Theme }>`
  color: #4caf50;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
`;

const LoginCard = styled.div<{ $theme: Theme }>`
  background: ${p => p.$theme.surface};
  border: 2px solid ${p => p.$theme.primary};
  border-radius: 12px;
  padding: 14px 18px;
  text-align: center;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LoginBtn = styled.button<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #000;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Arrow = styled.div<{ $theme: Theme }>`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 12px solid ${p => p.$theme.primary};
`;

interface Props {
  theme: Theme;
  isLoggedIn: boolean;
  userHandle?: string;
  onLogin: () => void;
}

export const ContestPredictionInput: React.FC<Props> = ({
  theme,
  isLoggedIn,
  userHandle,
  onLogin
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0, visible: false });
  const [priceInput, setPriceInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEntry, setUserEntry] = useState<{ price_prediction: number } | null>(null);
  const [contest, setContest] = useState<{ id: string; is_open: boolean } | null>(null);

  // Fetch contest
  useEffect(() => {
    fetch('/api/contest/active')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.contest) {
          setContest(d.contest);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch user entry
  useEffect(() => {
    if (isLoggedIn && contest) {
      const token = localStorage.getItem('authToken');
      fetch(`/api/contest/${contest.id}/my-entry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.entry) {
            setUserEntry(d.entry);
          }
        })
        .catch(() => {});
    }
  }, [isLoggedIn, contest]);

  // Calculate position based on the contest line in the DOM
  useEffect(() => {
    const updatePosition = () => {
      // Find the contest line in any SVG on the page
      const contestLine = document.querySelector('.contest-target-group .contest-line');
      if (!contestLine) {
        setPosition(p => ({ ...p, visible: false }));
        return;
      }

      const x1 = parseFloat(contestLine.getAttribute('x1') || '0');
      
      // Position the input to the RIGHT of the line (add 180px to clear the line + label)
      setPosition({
        x: x1 + 60 + 180, // Chart margin (60) + offset to right of line (180)
        y: 100, // Below the top label
        visible: true
      });
    };

    // Update on mount and periodically (for zoom/pan changes)
    updatePosition();
    const interval = setInterval(updatePosition, 500);
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async () => {
    if (!contest || !priceInput) return;
    const price = parseInt(priceInput.replace(/,/g, ''));
    if (isNaN(price) || price < 1000) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const r = await fetch(`/api/contest/${contest.id}/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ price_prediction: price })
      });
      const d = await r.json();
      if (d.success) {
        setUserEntry({ price_prediction: price });
      }
    } catch {} 
    finally { setIsSubmitting(false); }
  };

  const formatNumber = (n: string) => {
    const clean = n.replace(/[^0-9]/g, '');
    return clean ? parseInt(clean).toLocaleString() : '';
  };

  if (!position.visible || !contest?.is_open) return null;

  return (
    <Container $x={position.x} $y={position.y} $theme={theme}>
      {userEntry ? (
        // Already submitted
        <SuccessCard $theme={theme}>
          <SuccessIcon>✅</SuccessIcon>
          <SuccessPrice $theme={theme}>${userEntry.price_prediction.toLocaleString()}</SuccessPrice>
          <SuccessText $theme={theme}>Your prediction is locked!</SuccessText>
        </SuccessCard>
      ) : isLoggedIn ? (
        // Logged in, show input
        <InputCard $theme={theme}>
          <Title $theme={theme}>🎯 Your Price Prediction</Title>
          <Subtitle $theme={theme}>What will BTC be on April 28?</Subtitle>
          <UserBadge $theme={theme}>✓ @{userHandle}</UserBadge>
          <InputRow>
            <PriceWrapper $theme={theme}>
              <Prefix $theme={theme}>$</Prefix>
              <Input
                $theme={theme}
                type="text"
                placeholder="95,000"
                value={priceInput}
                onChange={(e) => setPriceInput(formatNumber(e.target.value))}
                maxLength={10}
                autoFocus
              />
            </PriceWrapper>
            <SubmitBtn
              $theme={theme}
              $disabled={!priceInput || isSubmitting}
              onClick={handleSubmit}
              disabled={!priceInput || isSubmitting}
            >
              {isSubmitting ? '...' : '🔒 Lock'}
            </SubmitBtn>
          </InputRow>
        </InputCard>
      ) : (
        // Not logged in
        <LoginCard $theme={theme}>
          <Title $theme={theme}>🎯 Predict the Price</Title>
          <Subtitle $theme={theme}>Win 100k sats!</Subtitle>
          <LoginBtn $theme={theme} onClick={onLogin}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Sign in with X
          </LoginBtn>
        </LoginCard>
      )}
      <Arrow $theme={theme} />
    </Container>
  );
};

export default ContestPredictionInput;
